import './featured-contents-highlight.scss'

import React from 'react'

import { ImageArtDirection } from '@/components/image/image-art-direction'
import ImageLabel from '@/components/image-label'
import Link from '@/components/link/link'
import TitleLink from '@/components/title-link/title-link'
import { typeNameMapping, useDefaultAllImages } from '@/hooks/useDefaultImages'
import { generateUrl } from '@/internal/utils/generate-url'
import { getModelMessages } from '@/utils/get-model-messages'
import { isUpcomingEvent } from '@/utils/upcoming-event'

type Props = {
  title: Queries.SectionFeaturedContentsFragment['title']
  filter: Queries.SectionFeaturedContentsFragment['filter']
  nodes: readonly (Highlights | null)[]
}

const FeaturedContentsHighlight: React.FC<Props> = (props) => {
  const allDefaultImages = useDefaultAllImages() ?? {}
  const { title, filter, nodes } = props
  const modelMessages = getModelMessages(filter?.__typename)
  return (
    <div className="container highlighteds-v1">
      <div className="row">
        <div className="col">
          <h2>{title}</h2>
        </div>
      </div>
      {filter && (
        <div className="row link-read-more">
          <div className="col">
            <Link to={generateUrl(filter?.__typename, filter?.slug)}>
              {modelMessages.button}
            </Link>
          </div>
        </div>
      )}
      <div className="row items mt-lg-2">
        {nodes.slice(0, 3).map((item, index) => {
          if (item === null) return null
          const classItem = nodes.length > 2 ? 'col-lg-3' : 'col-lg-6'
          const modelMessages = getModelMessages(item.__typename)
          const url = generateUrl(item.__typename, item.slug)
          const imgFeatured =
            item.featured?.gatsbyImageData ??
            allDefaultImages[typeNameMapping[item.__typename]]
              ?.imgFeaturedMultipleContentFeatured.desktop
          const imgFeaturedMobile =
            item.featured?.mobile ??
            allDefaultImages[typeNameMapping[item.__typename]]
              ?.imgFeaturedMultipleContentFeatured.mobile
          const image =
            item.image?.gatsbyImageData ??
            allDefaultImages[typeNameMapping[item.__typename]]
              ?.imgFeaturedMultipleContent.desktop
          const imageMobile =
            item.image?.mobile ??
            allDefaultImages[typeNameMapping[item.__typename]]
              ?.imgFeaturedMultipleContent.mobile
          return (
            <div
              key={`highligthed-${item.slug}`}
              className={index === 0 ? 'col-lg-6 col-first' : classItem}
            >
              <div className="image-container">
                <Link to={url}>
                  <ImageArtDirection
                    className={index === 0 ? 'imgfeatured' : ''}
                    image={index === 0 ? imgFeatured : image}
                    imageMobile={index === 0 ? imgFeaturedMobile : imageMobile}
                    alt={item.externalName!}
                  />
                </Link>
                {/* Show upcoming event label for events marked as pre-events */}
                {isUpcomingEvent(item) && <ImageLabel text="Upcoming Event" />}
              </div>

              <TitleLink
                to={url}
                title={item.externalName!}
                htmltag={'h3'}
                model={modelMessages.label}
              />

              <p className="descr">{item.excerpt}</p>
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default FeaturedContentsHighlight
